import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendDown, Users, FunnelSimple } from "@phosphor-icons/react";

export default function FunnelPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/funnel?days=${days}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || !data) {
    return (
      <div className="border border-slate-200 rounded-xl bg-white p-6">
        <p className="text-sm text-slate-500">Loading funnel…</p>
      </div>
    );
  }

  const max = Math.max(...data.steps.map((s) => s.unique_users), 1);

  return (
    <div className="border border-slate-200 rounded-xl bg-white p-5" data-testid="funnel-panel">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FunnelSimple size={22} weight="duotone" className="text-[#C4A265]" />
          <div>
            <h3 className="font-heading text-lg text-slate-900">Activation Funnel</h3>
            <p className="text-xs text-slate-500">Where users land, drop off, and convert</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="funnel-range"
            className="px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-body text-slate-700 focus:outline-none focus:border-[#C4A265]"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">New users</p>
          <p className="font-heading text-xl text-slate-900 flex items-center gap-1">
            <Users size={16} weight="duotone" className="text-[#C4A265]" />
            {data.new_users}
          </p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Top of funnel</p>
          <p className="font-heading text-xl text-slate-900">{data.anchor_users}</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Final step</p>
          <p className="font-heading text-xl text-slate-900">
            {data.steps[data.steps.length - 1].unique_users}
            <span className="text-xs text-slate-500 font-body ml-1">
              ({data.steps[data.steps.length - 1].conversion_pct}%)
            </span>
          </p>
        </div>
      </div>

      {/* Funnel rows */}
      <div className="space-y-2">
        {data.steps.map((s, i) => {
          const pct = (s.unique_users / max) * 100;
          const dropoff =
            s.step_pct !== null && s.step_pct !== undefined && s.step_pct < 100
              ? Math.round(100 - s.step_pct)
              : null;
          return (
            <div key={s.key} className="group" data-testid={`funnel-step-${s.key}`}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm text-slate-800">
                  <span className="text-slate-400 mr-2">{i + 1}.</span>
                  {s.label}
                </span>
                <span className="text-xs text-slate-500">
                  <span className="font-medium text-slate-900">{s.unique_users}</span> users · {s.conversion_pct}%
                  {s.step_pct !== null && s.step_pct !== undefined && s.step_pct <= 100 && (
                    <span className="ml-2 text-slate-400">step: {s.step_pct}%</span>
                  )}
                  {s.step_pct !== null && s.step_pct !== undefined && s.step_pct > 100 && (
                    <span className="ml-2 text-slate-400" title="Step has more users than the prior step — likely because earlier instrumentation wasn't live yet.">data backfill</span>
                  )}
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#C4A265] to-[#B89A7E] transition-[width] duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {dropoff !== null && dropoff >= 60 && i > 0 && (
                <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                  <TrendDown size={11} /> {dropoff}% drop from prior step — investigate
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
